import * as is from 'utilities/is';
import semver2int from 'semver2int';
import { HOUR_MS, later } from 'utilities/time';
import { PLAYER_TYPES } from 'constants/';
import { logger } from 'utilities/logging';
import { pageInformation, prefixedQueryParams } from 'utilities/context';
import { makeUniqueId, propertyCountByProp } from 'utilities/data';
import { LEGACY_ENVIRONMENTS } from 'configuration/environment';
import { APPLICATION_EVENT, ANNOTATION_EVENT, PLAYER_EVENT } from 'constants/events';
import { isAnnotationTrackable } from 'constants/annotations';
import { createReporter, MODULE_NAMES } from './reporter_factory';
import { recordResponse } from './reporters/user_response_reporter';
import { PlaybackTracker } from './playback_tracker';
import { AnnotationsTracker } from './annotations_tracker';
import { LivePlaybackTracker } from './live_playback_tracker';
import { SessionTracker } from './session_tracker';
import { getContextProvider } from './context_providers/user_context';

export const RELEVANT_PLAYER_EVENTS = [
    PLAYER_EVENT.PLAY,
    PLAYER_EVENT.PAUSE,
    PLAYER_EVENT.TIME_UPDATE,
    PLAYER_EVENT.SEEKED,
    PLAYER_EVENT.ENDED
];

export class TrackingManager {
    constructor(options, eventBus) {
        this.options = options;
        this.eventBus = eventBus;
        this.options.bus = eventBus;
        this.environment = LEGACY_ENVIRONMENTS[options.environment];
        this.reporters = [];
        this.playerEvents = {};
        this.context = {};
        this.eventCount = 0;
        this.queuing = true;
        this.eventQueue = [];
        this.sessionTracker = new SessionTracker();
        this.annotationsTracker = new AnnotationsTracker();
        this.setupListeners(eventBus);
    }

    setupListeners(bus) {
        bus.once(APPLICATION_EVENT.LOAD, this.onApplicationLoad);
        bus.once(PLAYER_EVENT.AVAILABLE, this.onPlayerAvailable);
        bus.on(PLAYER_EVENT.ENDED, this.onPlayerEnded);
        bus.on(ANNOTATION_EVENT.ANNOTATIONS_UPDATED, this.onAnnotationsUpdate);
        bus.on(ANNOTATION_EVENT.ANNOTATION_ACTION, this.onAnnotationAction);
        bus.on(APPLICATION_EVENT.STATE_CHANGE, this.onStateChange);
        bus.on(ANNOTATION_EVENT.RECORD_QUIZ_RESPONSE, this.onRecordQuizResponse);
        RELEVANT_PLAYER_EVENTS.forEach(event => {
            this.playerEvents[event] = this.onPlayerEvent.bind(this, event); // store event handler for de-registering
            bus.on(event, this.playerEvents[event]);
        });
    }

    removeListeners(bus) {
        bus.off(APPLICATION_EVENT.LOAD, this.onApplicationLoad);
        bus.off(PLAYER_EVENT.AVAILABLE, this.onPlayerAvailable);
        bus.off(PLAYER_EVENT.ENDED, this.onPlayerEnded);
        bus.off(ANNOTATION_EVENT.ANNOTATIONS_UPDATED, this.onAnnotationsUpdate);
        bus.off(ANNOTATION_EVENT.ANNOTATION_ACTION, this.onAnnotationAction);
        bus.off(APPLICATION_EVENT.STATE_CHANGE, this.onStateChange);
        RELEVANT_PLAYER_EVENTS.forEach(event => {
            bus.off(event, this.playerEvents[event]);
        });
    }

    onApplicationLoad = ({ analytics, group, project, annotations, state, ...config }) => {
        annotations = (annotations || []).filter(isAnnotationTrackable);

        if (!analytics || !group || !project) {
            logger.warn('missing required information to setup tracking');
            return;
        }

        this.configuration = analytics;
        this.variables = config.variables || {};

        if (analytics.disableAnalytics) {
            this.removeListeners(this.eventBus);
            return;
        }

        const modules = [...analytics.modules, MODULE_NAMES.BIG_QUERY, MODULE_NAMES.LOCAL_BROADCAST];

        this.packageSettings = config.package || {};
        this.analyticsSettings = analytics;
        this.setInitialContext({ group, project, annotations });
        this.setLocalization(state);

        Promise.all([
            this.setupUserContext(),
            this.createReportersFromModules(modules)
        ])
        .then(this.trackLoad)
        .then(this.consumeEventQueue);
    }

    onPlayerAvailable = (player, adapter) => {
        this.player = adapter;
        this.playbackTracker = adapter.mediaType === PLAYER_TYPES.LIVE ? new LivePlaybackTracker(adapter) : new PlaybackTracker(adapter);
        this.annotationsTracker.player = adapter;
    }

    onPlayerEnded = () => {
        // Ensure all remaining annotations fire "Annotation Ended"
        this.onAnnotationsUpdate([]);
    }

    createReportersFromModules(modules = []) {
        return (
            Promise
                .all(modules.map(createReporter(this.configuration, this.options)))
                .then(reporters => this.reporters = this.reporters.concat(reporters))
        );
    }

    onPlayerEvent(eventName, event, playerAdapter) {
        const responses = this.playbackTracker.getEventResponses(eventName, playerAdapter);
        this.updatePlaybackStats(playerAdapter);

        responses
            .map(a => [a.action, a.data])
            .forEach(params => this.trackPlayerAction('Video', ...params));
    }

    updatePlaybackStats(player) {
        this.setContext(this.playbackTracker.getPlaybackStats(player));
    }

    onAnnotationsUpdate = (currentAnnotations) => {
        const responses = this.annotationsTracker.getAnnotationUpdateEventResponses(currentAnnotations.filter(isAnnotationTrackable));

        responses
            .map(a => [a.action, a.data])
            .forEach(params => this.trackAnnotationEvent(...params));
    }

    onAnnotationAction = (action, annotation, ...args) => {
        if (!isAnnotationTrackable(annotation)) {
            return;
        }

        const responses = this.annotationsTracker.getAnnotationActionEventResponses(action, annotation, ...args);

        responses
            .map(a => [a.data, a.action])
            .forEach(params => this.trackAnnotationEvent('Annotation Action', ...params));
    }

    consumeEventQueue = () => {
        this.queuing = false;

        this.eventQueue.forEach(([eventName, payload]) => {
            this.reportEvent(eventName, { ...payload, ...this.context });
        });

        this.eventQueue.length = 0;
    }

    trackLoad = () => {
        this.track('Player View', 'Load');
    }

    trackPlayerAction(type, action, data = {}) {
        this.track('Player Action', type, action, data);
    }

    trackAnnotationEvent(eventName, data, action = '') {
        this.track(eventName, '', action, data);
    }

    track(eventName, type, action = '', details = {}, customEventData = {}) {
        let payload = {
            ...details,
            ...this.context,
            ...this.sessionTracker.sessionContext,
            'Event Order': this.eventCount++,
            'Type': type || details.Type
        };

        const { uid } = this.configuration;

        if (!is.empty(customEventData)) {
            payload['Custom Event Data'] = customEventData;
        }

        if (!is.empty(this.partnerData)) {
            payload = { ...payload, ...this.partnerUser };
            payload['Partner Data'] = this.partnerData;
        }

        if (action) {
            payload['Action'] = action;
        }

        if (uid) {
            payload['Partner User ID'] = uid;
        }

        // Holdover from legacy implementation - https://github.com/hapyak/hapyak/blob/feature/views/static/js/tracking.js#L212
        // Unclear if we still need it so disabling for now
        // this.extendPlaySessionReset(); 

        if (this.userContext && eventName === 'Custom Action' || eventName === 'Annotation Action') {
            this.userContext.setActivatedUser();
        }

        if (this.queuing) {
            logger.debug('queued reporting', eventName, action, payload);
            this.eventQueue.push([eventName, payload]);
            return;
        }
        
        this.reportEvent(eventName, payload);
    }

    reportEvent(eventName, payload) {
        logger.debug('reporting', eventName, payload);
        this.reporters.forEach(r => r.track(eventName, payload));
    }

    setupUserContext() {
        this.userContext = getContextProvider(this.options.environment, this.configuration);

        return (
            this.userContext
                .getIdInfo()
                .then(this.storeUserContext)
                .catch(this.storeDummyUserContext)
        );
    }

    setInitialContext({ group, project, annotations = [] }) {
        const videoSource = 'html5';

        this.setContext({

            /* Base Context */
            'App ID': '2',
            'Embedded': true,
            'Context': 'Viewer',
            'Host': pageInformation.host,
            'Href': pageInformation.href,
            'Hapyak Version': semver2int(HAPYAK_VERSION), // provided in build/tools/plugins.js,

            /* Video Context */
            'Play Session': makeUniqueId(),
            'Player Type': videoSource,
            'Video UUID': project.videoId,
            'Video Title': project.videoTitle,
            'Video Source': videoSource,
            'Video Source ID': project.videoSource,
            'Video Offset': 0,
            'Video Accrued': 0,
            'Video Percent Complete': 0,

            /* Group Context */
            'Group ID': group.id,
            'Group Name': group.name,
            'Group Parent ID': group.parent,
            'Customer': {
                'id': group.customer
            },
            'Affiliate': {
                'id': group.affiliate
            },

            /* Track Context */
            'Num Annotations': annotations.length,
            'Annotation Types': propertyCountByProp(annotations, 'type'),

            /* Project Context */
            'Project UUID': project.id,
            'Project Title': project.title,
            'Project Tags': project.tags ? [project.tags] : [],
            'Project Ref ID': project.refId
        });
    }

    onStateChange = ({state}) => this.setLocalization(state)

    onRecordQuizResponse = args => {
        if (this.configuration?.disableAnalytics) {
            later(() => this.eventBus.emit(ANNOTATION_EVENT.RECORD_QUIZ_RESPONSE_SUCCEED));
            return;
        }

        const analyticsContext = this.context;

        if (this.variables?.respondentId) {
            analyticsContext.user = {
                id: this.variables?.respondentId
            }
        }

        if (this.variables?.contentId) {
            analyticsContext.contentId = this.variables?.contentId;
        }

        recordResponse({
            ...args,
            analyticsContext,
            packageSettings: this.packageSettings,
            analyticsSettings: this.analyticsSettings
        })
        .then(({status}) => {
            if (status >= 400) {
                throw 'Response Failed';
            }

            this.eventBus.emit(ANNOTATION_EVENT.RECORD_QUIZ_RESPONSE_SUCCEED);
        })
        .catch(() => this.eventBus.emit(ANNOTATION_EVENT.RECORD_QUIZ_RESPONSE_FAILED))
    }

    setLocalization({ language = 'en', level }) {
        this.context.level = level;
        this.setContext({
            loc: {
                lang: language
            }
        })
    }

    setContext(updates) {
        this.context = { ...this.context, ...updates };
    }

    removeContext(keys) {
        if (!is.array(keys)) {
            keys = [keys];
        }

        keys.forEach(k => delete this.context[k]);
    }

    extendPlaySessionReset() {
        const { player } = this,
            fiveHours = 5 * HOUR_MS;

        let timeout = fiveHours;

        if (player && player.duration) {
            timeout = Math.min(fiveHours, player.duration * 5000);
        }

        if (this.playSessionResetTimeout) {
            window.clearTimeout(this.playSessionResetTimeout);
        }

        this.playSessionResetTimeout = window.setTimeout(() => {
            this.setContext({ 'Play Session': makeUniqueId() });
        }, timeout);
    }

    storeUserContext = ({ uid, ru, iu }) => {
        this.setContext({
            user: {
                id: uid,
                returned: ru,
                interacted: iu
            }
        });
    }

    storeDummyUserContext = () => {
        this.setContext(this.userContext.userInfo); // use dummy userInfo if timedout
    }

    get partnerData() {
        const params = prefixedQueryParams('hapyak_');

        if (Object.keys(params).length) {
            return params;
        }

        if (this.variables?.respondentId) {
            return {
                userId: this.variables?.respondentId
            }
        }

        return {};
    }

    get partnerUser() {
        const partnerData = this.partnerData;
        let partner = {};

        if (!is.empty(partnerData)) {
            if (partnerData.userId) {
                partner['Partner User ID'] = partnerData.userId;
            }

            if (partnerData.email) {
                partner['Partner Email'] = partnerData.email;
            }

            if (partnerData.username) {
                partner['Partner Username'] = partnerData.username;
            }
        }

        return partner;
    }
}